import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Text, Checkbox, Stack, Group, Button, ScrollArea, Badge, Alert } from '@mantine/core';
import { ageAsOf, getAgeDivision } from '../../config/categories';
import { studentKey } from '../../services/schoolService';

/**
 * "Add from last year": tick students from earlier seasons to copy their
 * details into this season's roster. Students already on the roster (same
 * name + birthday) can't be ticked, so running it twice can't duplicate.
 */
const CarryOverModal = ({ opened, onClose, pastStudents, currentKeys, year, onAdd }) => {
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const rows = useMemo(() => pastStudents.map(p => {
    const key = studentKey(p.dancerInfo);
    const dob = p.dancerInfo.date_of_birth;
    return {
      key,
      past: p,
      name: `${p.dancerInfo.first_name || ''} ${p.dancerInfo.last_name || ''}`.trim(),
      age: dob ? ageAsOf(dob, year) : null,
      division: dob ? getAgeDivision(dob, year) : null,
      onRoster: currentKeys.has(key),
    };
  }), [pastStudents, currentKeys, year]);

  const available = rows.filter(r => !r.onRoster);

  useEffect(() => {
    if (opened) { setSelected(new Set()); setError(null); setProgress(null); }
  }, [opened]);

  const toggle = (key) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const allSelected = available.length > 0 && available.every(r => selected.has(r.key));

  const add = async () => {
    const chosen = available.filter(r => selected.has(r.key)).map(r => r.past);
    setAdding(true);
    setError(null);
    try {
      await onAdd(chosen, (done) => setProgress({ done, total: chosen.length }));
      onClose();
    } catch (err) {
      setError(err.message || 'Some students could not be added. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Modal opened={opened} onClose={adding ? () => {} : onClose} centered size="lg"
      title={<Text fw={700} size="lg">Add students from last year</Text>}>
      <Stack gap="sm">
        <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>
          Their name, birthday, contact, address, parent/guardian and emergency contact are copied. Level,
          training, medical info, solos and music are left for {year}, and parents of students under 18
          need a new consent email this season.
        </Text>
        {error && <Alert color="red" p="xs"><Text size="sm">{error}</Text></Alert>}
        {available.length > 0 && (
          <Checkbox
            color="gold"
            label={<Text size="sm" fw={600}>Select all ({available.length})</Text>}
            checked={allSelected}
            indeterminate={!allSelected && selected.size > 0}
            onChange={() => setSelected(allSelected ? new Set() : new Set(available.map(r => r.key)))}
            disabled={adding}
          />
        )}
        <ScrollArea.Autosize mah={360}>
          <Stack gap={6}>
            {rows.map(r => (
              <Group key={r.key} justify="space-between" wrap="nowrap" gap="sm"
                style={{ padding: '6px 8px', borderRadius: 6, background: selected.has(r.key) ? 'var(--soft-cream)' : 'transparent' }}>
                <Checkbox
                  color="gold"
                  checked={selected.has(r.key)}
                  onChange={() => toggle(r.key)}
                  disabled={r.onRoster || adding}
                  label={<Text size="sm" fw={500}>{r.name}</Text>}
                  description={[
                    `${r.past.year} roster`,
                    r.age != null && `${r.age} on Jan 1, ${year}`,
                    r.division,
                  ].filter(Boolean).join(' · ')}
                  styles={{ label: { textAlign: 'left' }, description: { textAlign: 'left' } }}
                />
                {r.onRoster && <Badge variant="light" color="gray" style={{ flexShrink: 0 }}>Already added</Badge>}
              </Group>
            ))}
          </Stack>
        </ScrollArea.Autosize>
        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={adding}>Cancel</Button>
          <Button color="gold" onClick={add} loading={adding} disabled={!selected.size}>
            {adding && progress ? `Adding ${progress.done} of ${progress.total}…` : `Add ${selected.size || ''} student${selected.size === 1 ? '' : 's'}`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CarryOverModal;
